import _startsWith from 'lodash/startsWith';
import _last from 'lodash/last';
import _mapValues from 'lodash/mapValues';
import _mapKeys from 'lodash/mapKeys';
import _toUpper from 'lodash/toUpper';
import _upperFirst from 'lodash/upperFirst';
import imagesloaded from 'imagesloaded';

/**
 * Build a URL from a route name and parameters.
 * @param {string} routeName
 * @param {object} parameters
 */
export function route (routeName, parameters = {}) {

    parameters = { company: 43534593, ...parameters };

    let route = Jobtoolz.routes[routeName]

    // Replace provided parameters.
    Object.keys(parameters)
          .forEach( key => route = route.replace( new RegExp( '{' + key + '\\??}' ), parameters[key] ))

    let protocol = typeof window !== 'undefined' ? window.location.protocol + '//' : 'https://';

    // Remove leftover optional parameters.
    return protocol + route.replace( /\/{[a-zA-Z]+\?}/, '' )
}

/**
 * Generate a random GUID.
 * @returns {string}
 */
export function guidGenerator() {
    let S4 = function() {
        return (((1+Math.random())*0x10000)|0).toString(16).substring(1);
    };
    return (S4()+S4()+"-"+S4()+"-"+S4()+"-"+S4()+"-"+S4()+S4()+S4());
}

/**
 * Return the appropriate class from a class attribute string containing
 * (Tailwind generated) responsive classname variants for a given
 * website preview type (phone/tablet/desktop).
 *
 * @param previewType string
 * @param query string
 */
export function responsive (previewType, query)
{
    // If window is not defined, this implies our application is not
    // being rendered in a browser and we can conclude that rendering
    // is happening on the server.
    // In that case we want to return the query as is to allow for
    // normal responsive behaviour on the serverside rendered site.
    if (typeof(window) === 'undefined') {
        return query;
    }

    let classes = query.split(' ');

    let result;

    switch (previewType) {
        case 'desktop':
            result = classes.find(c => _startsWith(c, 'lg:'))
            || classes.find(c => _startsWith(c, 'mdplus:'))
            || classes.find(c => _startsWith(c, 'md:'))
            || classes.find(c => _startsWith(c, 'sm:'))
            || classes.find(c => ! c.includes(':'))
            || '';
            break;

        case 'tablet':
            result = classes.find(c => _startsWith(c, 'md:'))
            || classes.find(c => _startsWith(c, 'sm:'))
            || classes.find(c => ! c.includes(':'))
            || '';
            break;

        case 'phone':
            result = classes.find(c => ! c.includes(':')) || '';
            break;
    }

    return _last(result.split(':'));
}

function normalizeItem (item)
{
    if (!item) {
        return null;
    }

    return {
        id: item.id,
        ...item.attributes,
        pivot: item.pivot,
        ..._mapValues(item.relationships, relation => normalizeJson(relation)),
        links: item.links,
    }
}

/**
 * Convert a JSON API spec response to a flatter structure for easy attribute access.
 *
 * @param json
 */
export function normalizeJson (json)
{
    if (Array.isArray(json)) {
        return json.map(normalizeItem);
    }

    return normalizeItem(json);
}

/**
 * Map an object with filters so that the keys look like this:
 * filter[filterName]
 *
 * @param Object filters
 */
export function mapFilters (filters)
{
    return _mapKeys(filters, (value, key) => 'filter[' + key + ']');
}

export function runningInBrowser ()
{
    return typeof window !== 'undefined';
}

/**
 * Generate a new Masonry layout on the given target.
 *
 * @param {HTMLElement} target
 */
export function masonryLayout (target)
{
    // We require Masonry here instead of using a static ES6 import, because the
    // package depends on the global window variable being set.
    // Using a static ES6 import with a window dependency fucks with our SSR as
    // there is no window in Node!
    let Masonry = require('masonry-layout');

    imagesloaded(target,  instance => {
        new Masonry(target, {
            itemSelector: '.grid-item',
            resize: true,
            fitWidth: true,
            gutter: 16
        });
    });
}

/**
 * Generate the necessary styles for a marquee slider on a given element.
 *
 * @param {HTMLElement} target
 */
export function marquee (target)
{
    let styles = target.querySelector('style').sheet;
    let id = target.querySelector('.marquee-container').id;
    let slides = target.querySelector('.marquee-slides');

    imagesloaded(slides, instance => {
        styles.insertRule(
            `@keyframes ${ id } {
                0% {
                    transform: translatex(0px);
                }
                100% {
                    transform: translateX(-${ slides.scrollWidth }px);
                }
            }`,
            0
        );

        styles.insertRule(
            `#${ id } {
                animation: ${ id } ${ slides.scrollWidth / 50 }s infinite linear;
            }`,
            0
        );
    });
}

/**
 *
 * Generate a new map with all of it's added functionality on a given target container.
 * The desired target is an instance of the Locations.vue component's element referenced as
 * "locations". This element should have the available locations set as a data attribute
 * (The access token is set in the blade template.)
 *
 * @param {*} target
 */
export function mapbox (target)
{
    let links = target.querySelectorAll('.location-link');
    let hasLinks = links.length ? true : false;
    let locations = JSON.parse(target.dataset.locations);

    let markerTemplate = '<svg style="display:block;" width="32" height="41"><defs><ellipse id="b" cx="12" cy="31" rx="11" ry="5"/><filter x="-34.1%" y="-75%" width="168.2%" height="250%" filterUnits="objectBoundingBox" id="a"><feOffset in="SourceAlpha" result="shadowOffsetOuter1"/><feGaussianBlur stdDeviation="2.5" in="shadowOffsetOuter1" result="shadowBlurOuter1"/><feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0" in="shadowBlurOuter1"/></filter></defs><g transform="translate(4)"><use filter="url(#a)" xlink:href="#b" fill="#000"/><path d="M12 30.5a.5.5 0 0 0 .355-.148 58.322 58.322 0 0 0 1.941-2.088 69.642 69.642 0 0 0 3.831-4.682c3.367-4.52 5.373-8.538 5.373-11.534C23.5 5.33 18.219.5 12 .5S.5 5.33.5 12.048c0 2.996 2.006 7.014 5.373 11.534a69.642 69.642 0 0 0 3.831 4.682c.825.926 1.498 1.639 1.94 2.087A.5.5 0 0 0 12 30.5z" stroke="#FFF" style="fill: currentColor;"/><path d="M8 12c0-2.206 1.794-4 4-4s4 1.794 4 4-1.794 4-4 4-4-1.794-4-4z" fill="#FFF"/></g></svg>';

    let markers = [];
    let popups = [];
    let bounds = new mapboxgl.LngLatBounds();

    let createPopup = location => {

        let popup =  new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: {
                'bottom': [0, -25],
                'top': [0, 17],
                'left': [17, -5],
                'right': [-17, -5],
                'top-right': [0, 15],
                'top-left': [0, 15],
                'bottom-right': [-10, -20],
                'bottom-left': [10, -20],
            },
        }).setLngLat([location.lng, location.lat]);

        let name = location.alias[window.lang.locale] || location.city;

        let popupContent = `<div w-240 class="bg-white site__rounded--small overflow-hidden shadow-md">
        <div class="site__bg--brand site__color--brand-contrast px-24 py-16 text-body font-semibold">${ name }</div>
        <div class="px-24 py-16 text-body-sm site__color--text leading-normal">`;

        if (hasLinks) {
            popupContent += location.street;
        } else {
            popupContent += `
            <div class="flex">
                <span>
                    <svg class="w-16 h-16 block fill-current mt-2 mr-8 flex-none" viewBox="0 0 14 16">
                        <path d="M7 0C3.1 0 0 3.1 0 7c0 1.9.7 3.7 2.1 5 .1.1 4.1 3.7 4.2 3.8.4.3 1 .3 1.3 0 .1-.1 4.2-3.7 4.2-3.8 1.4-1.3 2.1-3.1 2.1-5 .1-3.9-3-7-6.9-7zm0 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                    </svg>
                </span>

                <span>${ location.street }</span>
            </div>`;

            if (location.phone) {
                popupContent += `<div class="flex pt-8">
                    <span>
                        <svg class="w-16 h-16 block fill-current mr-8 flex-none" viewBox="0 0 16 16">
                            <path d="M15.285 11.89l-2.578-2.594a1 1 0 0 0-1.416-.002L9 11.585l-5-5 2.294-2.294a1 1 0 0 0 .001-1.414L3.715.293a1 1 0 0 0-1.415 0L.004 2.588 0 2.585c0 7.18 5.82 13 13 13l2.283-2.283a1 1 0 0 0 .002-1.412"/>
                        </svg>
                    </span>

                    <a href="tel:${ location.phone }">
                        <span>${ location.phone }</span>
                    </a>
                </div>`
            }

            if (location.email) {
                popupContent += `<div class="flex pt-8">
                    <span>
                        <svg class="w-16 h-14 block fill-current mr-8 flex-none" viewBox="0 0 16 14">
                            <path d="M15 0c.6 0 1 .4 1 1v1.5L8 6.9 0 2.4V1c0-.6.4-1 1-1h14zM7.5 8.9c.28.14.72.14 1 0L16 4.7V13c0 .6-.4 1-1 1H1c-.6 0-1-.4-1-1V4.7l7.5 4.2z"/>
                        </svg>
                    </span>

                    <a href="mailto:${ location.email }">
                        <span>${ location.email }</span>
                    </a>
                </div>`;
            }
        }

        popupContent += '</div></div>';

        popup.setHTML(popupContent);

        popups.push(popup);

        return popup;
    }

    locations.forEach(location => {

        // Create the marker from our marker template.
        let markerElement = document.createElement('div');
        markerElement.innerHTML = markerTemplate;
        let marker = new mapboxgl.Marker(markerElement)
            .setLngLat([location.lng, location.lat]);

        // Create a popup for this location.
        let popup = createPopup(location);

        // Extend our bounds with the location's longitude and latitude.
        bounds.extend(marker.getLngLat());
        markers.push(marker);

        // Add an event listener to the marker to handle clicks.
        marker.getElement().addEventListener('click', e => {
            e.preventDefault();
            selectLocation(location, popup);
        });

        // Add an event listener to any links for this locations.
        let link = Array.from(links).find(link => link.dataset.locationId == location.id);

        if (typeof link !== 'undefined') {
            link.addEventListener('click', e => {
                e.preventDefault();
                selectLocation(location, popup);
            });
        }
    });

    let selectLocation = (location, popup) => {

        showPopup(popup);

        if (hasLinks) {
            flyToLocation(location);
            setActiveLink(location);
        }
    }

    // Create the map instance.
    let map = new mapboxgl.Map({
        container: target.querySelector('.locations-map'),
        attributionControl: false,
        style: 'mapbox://styles/mapbox/light-v10',
        failIfMajorPerformanceCaveat: false,
        zoom: 14,
        renderWorldCopies: true,
        scrollZoom: false,
        bounds,
        fitBoundsOptions: {padding: 100, maxZoom: 12}
    });

    // Fly to a given location on the map.
    let flyToLocation = location => {
        map.flyTo({
            speed: 2,
            center: [location.lng, location.lat],
            zoom: 15
        });
    }

    // Give the link corresponding to a given location a specific set of classes
    // to indicate its active state.
    let setActiveLink = location => {
        links.forEach(link => {
            link.classList.remove('site__bg--brand', 'site__color--brand-contrast');
        });

        Array.from(links)
            .find(link => link.dataset.locationId == location.id)
            .classList.add('site__bg--brand', 'site__color--brand-contrast');
    }

    // Show a popup, and hide all others.
    let showPopup = popup => {
        popups.forEach(item => item.remove());
        popup.addTo(map);
    }

    // Add controls to the map.
    map.addControl(new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
    }), 'top-left');

    // Add the markers to the map.
    markers.forEach(function (marker) {
        marker.addTo(map);
    });

    // If there's only one location, we will show the corresponding popup immediately.
    if (locations.length === 1) {
        showPopup(popups[0]);
    }
}

export let openableFileExtensionsInWebBrowser = [
    'pdf',
    //image
    'jpg',
    'jpeg',
    'png',
    'svg',
    'tif',
    'tiff',
    'bmp',
    'gif',
    //audio
    'aac',
    'm4a',
    'f4a',
    'mp3',
    'ogg',
    'oga',
    //video
    'mp4',
    'm4v',
    'mov',
    'qt',
    'mpeg',
    'mpg',
    'vob',
    'flv',
    'wmv',
    'asf',
    'mkv'
];

export let fonts = {

    fontParts (font) {
        return font.split(' ');
    },

    isSystemFont (font) {

        let parts = this.fontParts(font);

        return parts.length === 2
                && parts[0] === 'System'
                && ['Sans', 'Serif', 'Mono'].includes(parts[1]);
    },

    systemFontStack (font) {

        return {
            'sans': 'Inter, sans-serif',
            'serif': 'Constantia, Lucida Bright, Lucidabright, Lucida Serif, Lucida, DejaVu Serif, Bitstream Vera Serif, Liberation Serif, Georgia, serif',
            'mono': 'Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace'
        }[this.fontParts(font)[1].toLowerCase()];
    }
}

export function copyToClipboard (text, subject = null) {

    let copy = require('copy-to-clipboard');

    if (copy(text, {
        debug: true,
        message: 'THIS IS THE MESSAGE',
    })) {
        if (window.bus !== undefined) {
            bus.dispatch('copied-to-clipboard', subject);
        }
    }
}

export function buildDropdownOptions (options, firstLabel = null, firstValue = null)
{
    let result = [];

    if (firstLabel) {
        result.push({
            label: firstLabel,
            value: firstValue
        });
    }

    for (let key in options) {
        result.push({
            label: options[key].toString(),
            value: key.toString(),
        });
    }

    return result;
}

export function getCountriesSelectOptions(i18n) {
    return buildDropdownOptions(i18n.tm('countries'), '-- ' + i18n.t('general.select') + ' --');
}

export function containRetinaImages (target)
{
    imagesloaded(target, instance => {
        // on load
        resetRetinaImages(target);

        // on window resize
        // max-width and max-height in absolute elements does some weird stuff
        // only fix I can think of is resetting the max-width and max-height
        // on resize as well
        window.addEventListener('resize', function ()
        {
            resetRetinaImages(target);
        });
    });
}

export function ucfirst (str)
{
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function resetRetinaImages (target)
{
    target.querySelectorAll('img').forEach(image => {
        var maxWidth = (image.naturalWidth / 2),
            maxHeight = (image.naturalHeight / 2)
            ;

        // double check parent element
        if (maxWidth > image.parentNode.offsetWidth) {
            maxWidth = image.parentNode.offsetWidth;
        }
        if (maxHeight > image.parentNode.offsetHeight) {
            maxHeight = image.parentNode.offsetHeight;
        }

        image.style.maxWidth = maxWidth + 'px';
        image.style.maxHeight = maxHeight + 'px';

        // remove the full width/height
        image.style.width = 'auto';
        image.style.height = 'auto';

        image.style.opacity = 1;
    });
}

export function translated(translations, locale, useFallback = true, backupLocale)
{
    const text = translations[locale];

    if (text && text.replace(/(<([^>]+)>)/gi, "")) {
        return text;
    } else if (useFallback) {
        return translations[backupLocale] || '';
    }

    return '';
}

export function findTranslation(translations, locale, label, useFallback = true, backupLocale)
{
    const text = translations.find(translation => translation.locale === locale)[label];

    if (!text && useFallback) {
        return translations.find(translation => translation.locale === backupLocale)[label] || null;
    } else {
        return text || null;
    }

}

export function normalizeUrl (url) {
    if (!url) {
        return '';
    }

    if (url.startsWith('http')) {
        return url;
    }

    if (url.startsWith('//')) {
        return 'https:' + url;
    }

    return 'https://' + url;
}
